import { useCallback, useState, useMemo } from 'react';
import I18n from 'I18n';
import { useSelector } from 'react-redux';
import debounce from 'react-utils/debounce';
import UISelect from 'UIComponents/input/UISelect';
import UIFormControl from 'UIComponents/form/UIFormControl';
import { loadOptions } from '../operators/assigneeFilterOptionOperators';
import { useFilterValues } from 'find-and-filter-data/search-and-filter-data/public';
import { getAssignedToBot, getAssignee, getUnassigned } from 'find-and-filter-data/search-and-filter-schema/protected';
import { getCurrentInboxId } from 'conversations-inbox-lib/unified-inbox/public/selectors';
import { getUserId } from '../../selectors/userSelectors/getUserId';
import { getUserEmail } from '../../selectors/userSelectors/getUserEmail';
import { getUser } from '../../selectors/userSelectors/getUser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AsyncUIAvatar } from 'svh-workspace-ui-lib/code-splitting/public';
import { ASSIGNEE_PLACEHOLDER, UPDATE_FILTER_DEBOUNCE_MS } from '../constants/filterConstants';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'conv... Remove this comment to see the full error message
import { mrHubBot } from 'conversations-message-history/constants/images';
import isEmpty from 'transmute/isEmpty';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getClickedAssigneeWithSearchFilter, getCheckedAssigneeDeactivated } from 'conversations-inbox-lib/usage-tracking/inboxInteractionEvents';
import { getAssigneeFullName } from '../operators/getAssigneeFullName';
import UICheckbox from 'UIComponents/input/UICheckbox';
import { jsx as _jsx } from "react/jsx-runtime";
export const addAvatarAndActivationStatusToOption = () =>
// eslint-disable-next-line react/display-name
option => {
  let text = option.text;
  if (option.responder.activationStatus === 'DEACTIVATED') {
    text = `${option.text} (${I18n.text('conversations-inbox-ui.thread-search-and-filtering.filter-sidebar.filter-options.assignee.deactivated')})`;
  } else if (option.responder.activationStatus === 'REMOVED') {
    text = `${option.text} (${I18n.text('conversations-inbox-ui.thread-search-and-filtering.filter-sidebar.filter-options.assignee.removed')})`;
  }
  return Object.assign({}, option, {
    avatar: option.responder && option.responder.email &&
    /*#__PURE__*/
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ size: string; email: string; }' is not ass... Remove this comment to see the full error message
    _jsx(AsyncUIAvatar, {
      size: "xs",
      email: option.responder.email
    }),
    text
  });
};
const getOptionValue = filterValues => {
  const BOT_USER_ID = -2;
  const UNASSIGNED_USER_ID = -1;
  if (getAssignedToBot(filterValues)) return BOT_USER_ID;
  if (getUnassigned(filterValues)) return UNASSIGNED_USER_ID;
  return getAssignee(filterValues);
};
const debouncedLoadOptions = debounce((searchQuery, callback, pagination, inboxId, currentUserOption, setError) => {
  loadOptions({
    searchQuery,
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ size: string; src: any; }' is not assignab... Remove this comment to see the full error message
    botIcon: /*#__PURE__*/_jsx(AsyncUIAvatar, {
      size: "xs",
      src: mrHubBot
    }),
    callback,
    inboxId,
    currentUserOption,
    pagination,
    optionTransformation: addAvatarAndActivationStatusToOption()
  }).catch(() => setError(true));
}, UPDATE_FILTER_DEBOUNCE_MS);
const AssigneeFilter = () => {
  const {
    filterValues,
    setFilterValue
  } = useFilterValues();
  const [error, setError] = useState(false);
  const [inputQuery, setInputQuery] = useState('');
  const inboxId = useSelector(getCurrentInboxId);
  const userId = useSelector(getUserId);
  const userEmail = useSelector(getUserEmail);
  const user = useSelector(getUser);
  const userFullName = getAssigneeFullName(user);
  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState(false);
  const currentUserOption = useMemo(() => ({
    text: userFullName,
    value: userId,
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ size: string; email: string; }' is not ass... Remove this comment to see the full error message
    avatar: /*#__PURE__*/_jsx(AsyncUIAvatar, {
      size: "xs",
      email: userEmail
    })
  }), [userFullName, userId, userEmail]);
  const handleLoadOptions = useCallback((searchQuery, callback, pagination) => debouncedLoadOptions(searchQuery, callback, pagination, inboxId, currentUserOption, setError), [inboxId, currentUserOption, setError]);
  const onChange = e => {
    const {
      value
    } = e.target;
    if (value) setFilterValue('assignedAgentId', value);else setFilterValue('assignedAgentId', null);
    if (!isEmpty(inputQuery)) {
      trackEvent(getClickedAssigneeWithSearchFilter(inputQuery));
    }
  };
  const onChecked = useCallback(e => {
    setShowDeactivatedUsers(e.target.checked);
    if (e.target.checked === true) {
      trackEvent(getCheckedAssigneeDeactivated());
    }
  }, [setShowDeactivatedUsers]);
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "conversations-inbox-ui.thread-search-and-filtering.filter-sidebar.filter-options.assignee.title"
    }),
    children: /*#__PURE__*/_jsx(UISelect, {
      "data-test-id": "assignee-filter-dropdown",
      dropdownFooter: /*#__PURE__*/_jsx(UIFormControl, {
        children: /*#__PURE__*/_jsx(UICheckbox, {
          checked: showDeactivatedUsers,
          onChange: onChecked,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            "data-test-id": "filterInactiveAssigneesCheckboxLabel",
            message: "conversations-inbox-ui.thread-search-and-filtering.filter-sidebar.filter-options.assignee.filter-inactive-users"
          })
        })
      }),
      clearable: true,
      error: error,
      filterOptions: options => options.filter(option => {
        return showDeactivatedUsers || !option.responder || option.responder.activationStatus === 'ACTIVE';
      }),
      loadOptions: handleLoadOptions,
      onChange: onChange,
      onInputChange: input => setInputQuery(input),
      placeholder: ASSIGNEE_PLACEHOLDER,
      placement: "bottom right",
      searchPlaceholder: I18n.text('conversations-inbox-ui.thread-search-and-filtering.filter-sidebar.filter-options.assignee.search-placeholder'),
      defaultValue: null,
      menuWidth: "auto",
      value: getOptionValue(filterValues)
    })
  });
};
AssigneeFilter.displayName = 'AssigneeFilter';
export default AssigneeFilter;